/**
 * Precomputed course geometry + path sampling for The Gauntlet.
 * All path queries are O(1)/lerp — no per-frame allocations of path arrays.
 */

export const BOTS_PER_LANE = 100;
export const MAX_LANES = 3;

/** Lane center Z offsets (left / middle / right). */
export const LANE_Z = [-2.4, 0, 2.4] as const;

/** Course progress bookmarks (0 = start, 1 = summit). */
export const PROGRESS = {
  stage1Start: 0,
  stage1End: 0.28,
  gate1: 0.3,
  stage2Start: 0.32,
  stage2End: 0.56,
  gate2: 0.58,
  stage3Start: 0.6,
  cliffLedge: 0.78,
  stage3End: 0.92,
  summit: 1,
} as const;

export const LANE_COLORS = ['#3d9eff', '#ff6b4a', '#5dff9a'] as const;

// World extents — side-on three-quarter camera frames the full run.
const X0 = -16;
const X1 = 18;
const Y_FLOOR = 0.35;
const Y_RIDGE = 2.6;
const Y_CLIFF_TOP = 8.4;
const Y_SUMMIT = 8.8;

export interface PathSample {
  x: number;
  y: number;
  z: number;
}

/** Precomputed path samples along the course centerline (lane Z added at sample time). */
const PATH_STEPS = 128;
const pathX = new Float32Array(PATH_STEPS + 1);
const pathY = new Float32Array(PATH_STEPS + 1);

function heightAt(p: number): number {
  if (p <= PROGRESS.stage1End) {
    // Gentle flat run with subtle undulation.
    return Y_FLOOR + Math.sin(p * 40) * 0.04;
  }
  if (p <= PROGRESS.gate1) {
    return Y_FLOOR;
  }
  if (p <= PROGRESS.stage2End) {
    // Elevated ridge — steeper climb with gaps (dip notches).
    const t = (p - PROGRESS.stage2Start) / (PROGRESS.stage2End - PROGRESS.stage2Start);
    const base = Y_FLOOR + t * (Y_RIDGE - Y_FLOOR);
    const gap = Math.sin(t * Math.PI * 3) < -0.55 ? -0.55 : 0;
    return base + gap;
  }
  if (p <= PROGRESS.gate2) {
    return Y_RIDGE;
  }
  if (p <= PROGRESS.stage3End) {
    // Near-vertical cliff face.
    const t = (p - PROGRESS.stage3Start) / (PROGRESS.stage3End - PROGRESS.stage3Start);
    const eased = t * t * (3 - 2 * t);
    return Y_RIDGE + eased * (Y_CLIFF_TOP - Y_RIDGE);
  }
  // Summit plateau.
  return Y_SUMMIT;
}

// Build path once at module load.
for (let i = 0; i <= PATH_STEPS; i += 1) {
  const p = i / PATH_STEPS;
  pathX[i] = X0 + p * (X1 - X0);
  pathY[i] = heightAt(p);
}

/** Sample course centerline at progress ∈ [0,1]; writes into `out` (no alloc). */
export function samplePath(progress: number, laneZ: number, out: PathSample): PathSample {
  const p = progress < 0 ? 0 : progress > 1 ? 1 : progress;
  const f = p * PATH_STEPS;
  const i0 = f | 0;
  const i1 = i0 >= PATH_STEPS ? PATH_STEPS : i0 + 1;
  const t = f - i0;
  out.x = pathX[i0] + (pathX[i1] - pathX[i0]) * t;
  out.y = pathY[i0] + (pathY[i1] - pathY[i0]) * t;
  out.z = laneZ;
  return out;
}

/**
 * Pit trench layout — below/front of each verifier gate so end-state piles
 * read as a physical bar chart. Shared by visuals + physics colliders.
 *
 * floorY = center of floor slab; wall tops ≈ course-level so 0.32-tall boxes stay in.
 */
export const PIT_FLOOR_Y = -0.25;
/**
 * Soft landing band for fast pile sleep. Includes pit heaps, course floors,
 * and the stage-3 cling ledge (~Y=5). Free-falling tumblers sit above this
 * only while still in the air.
 */
export const PIT_LANDING_Y_MAX = 5.6;

export interface PitSpec {
  x: number;
  /** Visual box center Y (not physics floor center). */
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
}

/** Stage → pit trench (world space centers / extents for visual mesh). */
export const PITS: readonly [PitSpec, PitSpec, PitSpec] = [
  // Camera-forward trenches (z>0) so heaps sit between course and default camera
  // and read as a physical bar chart of per-stage failures.
  //
  // Deliberately SMALL footprint + raised floor: ~45 boxes per layer, so the
  // stage-1 heap (~200 bodies across 3 lanes) stacks ~4-5 layers and rises
  // clearly above the visual rim, while stage-2/3 (~40-60) fill to rim level.
  // The physics walls extend far above the visual rim (invisible chimney), so
  // stacks stay contained and read as columns — the literal bar chart.
  // Gate 1 / stage 1 — largest expected heap (~62–73 × 3 lanes)
  { x: -5.5, y: 0.05, z: 4.6, w: 1.5, h: 0.7, d: 1.2 },
  // Gate 2 / stage 2 — small heap (~13–15 × 3)
  { x: 4.2, y: 0.05, z: 4.6, w: 1.5, h: 0.7, d: 1.2 },
  // Cliff / stage 3 — medium heap (~13–23 × 3). x nudged left of the cliff so
  // the heap clears the right-edge HUD cards in the default camera.
  { x: 9.0, y: 0.05, z: 4.6, w: 1.5, h: 0.7, d: 1.2 },
] as const;

/** Pit center used for impulse aim + scatter targets. */
export function pitCenter(stage: 0 | 1 | 2): { x: number; y: number; z: number } {
  const p = PITS[stage];
  return { x: p.x, y: PIT_FLOOR_Y + 0.4, z: p.z };
}

/** Pit centers under each stage (world space) — soft scatter targets inside trench. */
export function pitTarget(stage: 0 | 1 | 2, laneZ: number, botIndex: number): PathSample {
  const p = PITS[stage];
  // Spread must stay inside the (now small) trench interior: w/2=0.75, d/2=0.6.
  const spread = ((botIndex % 10) - 4.5) * 0.1;
  const depth = PIT_FLOOR_Y + 0.35 + (botIndex % 5) * 0.05;
  return {
    x: p.x + spread,
    y: depth,
    z: p.z + ((botIndex % 7) - 3) * 0.08 + laneZ * 0.06,
  };
}

/** Static mesh layout helpers for CourseGeometry. */
export const COURSE_MESH = {
  stage1: { x: -11, y: Y_FLOOR / 2, z: 0, w: 10, h: Y_FLOOR, d: 8.2 },
  stage2: { x: 0.5, y: (Y_FLOOR + Y_RIDGE) / 2, z: 0, w: 9.5, h: 0.35, d: 8.2 },
  stage3Wall: { x: 10.2, y: (Y_RIDGE + Y_CLIFF_TOP) / 2, z: 0, w: 1.4, h: Y_CLIFF_TOP - Y_RIDGE + 0.4, d: 8.2 },
  stage3Ledge: { x: 11.4, y: Y_RIDGE + (Y_CLIFF_TOP - Y_RIDGE) * 0.42, z: 0, w: 1.6, h: 0.18, d: 7.4 },
  summit: { x: 16.2, y: Y_SUMMIT, z: 0, w: 3.2, h: 0.28, d: 5.2 },
  podium: { x: 16.2, y: Y_SUMMIT + 0.55, z: 0, w: 1.4, h: 0.9, d: 1.4 },
  pit1: PITS[0],
  pit2: PITS[1],
  pit3: PITS[2],
  gate1X: -4.2,
  gate2X: 6.2,
  startX: X0 + 0.4,
  Y_FLOOR,
  Y_RIDGE,
  Y_CLIFF_TOP,
  Y_SUMMIT,
} as const;
